import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Crown, Check, Zap, BarChart3, Infinity, Shield, Phone, Loader2,
  Calendar, CreditCard, AlertTriangle, ChevronRight, Gift, Clock, TrendingUp,
  Eye, MessageSquare, DollarSign, Car, ArrowUpRight, ArrowDownRight, Minus,
  TrendingDown, Target, Users, ShoppingCart, PieChart, Sparkles, Star
} from 'lucide-react';

const DEALER_BENEFITS = [
  { icon: Infinity, text: 'Unlimited car listings', highlight: true },
  { icon: BarChart3, text: 'Sales analytics dashboard' },
  { icon: Shield, text: 'Dealer verification badge' },
  { icon: Zap, text: 'Priority customer support' },
  { icon: TrendingUp, text: 'Featured listing placement' },
  { icon: Phone, text: 'Direct customer inquiries' }
];

const PLAN_ICONS = {
  monthly: Calendar,
  yearly_upfront: Sparkles,
  yearly_installments: Clock
};

function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'gold' }) {
  const colorClasses = {
    gold: 'bg-gold/10 text-gold',
    green: 'bg-trust-green/10 text-trust-green',
    blue: 'bg-blue-400/10 text-blue-400',
    orange: 'bg-orange-400/10 text-orange-400'
  };

  return (
    <div className="bg-card border border-border/50 rounded-xl p-5 hover-lift">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${
            trend > 0 ? 'text-trust-green' : trend < 0 ? 'text-red-400' : 'text-muted-foreground'
          }`}>
            {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : 
             trend < 0 ? <ArrowDownRight className="w-3 h-3" /> : 
             <Minus className="w-3 h-3" />}
            {Math.abs(trendValue || trend)}%
          </div>
        )}
      </div>
      <p className="font-mono-display text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{title}</p>
      {subtitle && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function ListingPerformanceRow({ listing, index }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-border/30 last:border-0">
      <span className="text-xs text-muted-foreground w-6">#{index + 1}</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{listing.title}</p>
        <p className="text-xs text-muted-foreground">GHS {listing.price?.toLocaleString()}</p>
      </div>
      <div className="flex items-center gap-4 text-xs">
        <div className="text-center">
          <p className="font-semibold">{listing.views}</p>
          <p className="text-muted-foreground">views</p>
        </div>
        <div className="text-center">
          <p className="font-semibold">{listing.inquiries}</p>
          <p className="text-muted-foreground">inquiries</p>
        </div>
        <div className="text-center">
          <p className="font-semibold text-trust-green">{listing.conversion_rate?.toFixed(1)}%</p>
          <p className="text-muted-foreground">conv.</p>
        </div>
        <Badge variant="outline" className={`text-[10px] ${
          listing.status === 'sold' ? 'border-trust-green/50 text-trust-green' : 'border-gold/50 text-gold'
        }`}>
          {listing.status === 'sold' ? 'Sold' : 'Active'}
        </Badge>
      </div>
    </div>
  );
}

export default function DealerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPlan, setSelectedPlan] = useState('yearly_installments');
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    const reference = searchParams.get('reference');
    
    if (subscriptionStatus === 'success' && reference) {
      verifyPayment(reference);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await API.get('/dealer/plans');
      setPlans(res.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  useEffect(() => {
    if (subscription?.is_dealer) {
      fetchAnalytics();
    }
  }, [subscription]);

  const fetchSubscriptionStatus = async () => {
    try {
      const res = await API.get('/dealer/subscription/status');
      setSubscription(res.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await API.get('/dealer/analytics');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const verifyPayment = async (reference) => {
    try {
      setLoading(true);
      const res = await API.get(`/dealer/subscription/verify/${reference}`);
      if (res.data.success) {
        toast.success('Payment successful! Dealer subscription activated.');
        fetchSubscriptionStatus();
      } else {
        toast.error(res.data.message || 'Payment verification failed');
      }
    } catch (error) {
      toast.error('Payment verification failed');
    } finally {
      setLoading(false);
    }
  };

  const startFreeTrial = async () => {
    setStartingTrial(true);
    try {
      const res = await API.post('/dealer/subscription/start-trial');
      if (res.data.success) {
        toast.success('Free trial started! You now have dealer access.');
        fetchSubscriptionStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  const subscribe = async (planType = selectedPlan) => {
    setSubscribing(true);
    try {
      const res = await API.post('/dealer/subscription/subscribe', {
        phone_number: user?.phone || '',
        plan_type: planType
      });
      
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setSubscribing(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your dealer subscription?')) return;
    
    try {
      await API.post('/dealer/subscription/cancel');
      toast.success('Subscription cancelled');
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </main>
    );
  }

  const isDealer = subscription?.is_dealer;
  const isTrial = subscription?.subscription_status === 'trial';
  const isActive = subscription?.subscription_status === 'active';
  const isExpired = subscription?.subscription_status === 'expired';
  const neverSubscribed = !subscription?.subscription_status;

  // If user is a dealer, show the analytics dashboard
  if (isDealer && (isTrial || isActive)) {
    return (
      <main data-testid="dealer-dashboard" className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-2">
                    Dealer Dashboard
                    <Badge className="bg-gold text-black border-none">
                      {isTrial ? 'Trial' : 'Active'}
                    </Badge>
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {isTrial 
                      ? `Trial ends ${new Date(subscription.trial_end_date).toLocaleDateString()}`
                      : `Member since ${analytics?.dealer_since ? new Date(analytics.dealer_since).toLocaleDateString() : 'N/A'}`
                    }
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/create-listing">
                <Button className="bg-gold text-black hover:brightness-110 gap-2">
                  <Car className="w-4 h-4" /> New Listing
                </Button>
              </Link>
              {isTrial && (
                <Button 
                  onClick={subscribe} 
                  disabled={subscribing}
                  variant="outline"
                  className="border-gold/50 text-gold gap-2"
                >
                  {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Subscribe Now
                </Button>
              )}
            </div>
          </div>

          {/* Trial Warning Banner */}
          {isTrial && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gold/10 border border-gold/30 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-gold" />
                  <div>
                    <p className="font-medium text-gold">Free Trial Active</p>
                    <p className="text-sm text-muted-foreground">
                      Your trial ends on {new Date(subscription.trial_end_date).toLocaleDateString()}. Subscribe to keep your dealer benefits.
                    </p>
                  </div>
                </div>
                <Button onClick={() => subscribe('yearly_installments')} disabled={subscribing} size="sm" className="bg-gold text-black">
                  {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe from GHS 50/mo'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Analytics Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-card border border-border/50 mb-6">
              <TabsTrigger value="overview" className="data-[state=active]:bg-gold data-[state=active]:text-black gap-2">
                <PieChart className="w-4 h-4" /> Overview
              </TabsTrigger>
              <TabsTrigger value="listings" className="data-[state=active]:bg-gold data-[state=active]:text-black gap-2">
                <Car className="w-4 h-4" /> Listings
              </TabsTrigger>
              <TabsTrigger value="revenue" className="data-[state=active]:bg-gold data-[state=active]:text-black gap-2">
                <DollarSign className="w-4 h-4" /> Revenue
              </TabsTrigger>
            </TabsList>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-gold" />
              </div>
            ) : analytics ? (
              <>
                {/* Overview Tab */}
                <TabsContent value="overview">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <StatCard
                      title="Total Listings"
                      value={analytics.overview.total_listings}
                      subtitle={`${analytics.overview.active_listings} active, ${analytics.overview.sold_listings} sold`}
                      icon={Car}
                      color="gold"
                    />
                    <StatCard
                      title="Total Views"
                      value={analytics.overview.total_views.toLocaleString()}
                      subtitle={`${analytics.recent_activity.views_30d} in last 30 days`}
                      icon={Eye}
                      color="blue"
                    />
                    <StatCard
                      title="Inquiries"
                      value={analytics.overview.total_inquiries}
                      subtitle={`${analytics.recent_activity.inquiries_30d} in last 30 days`}
                      icon={MessageSquare}
                      color="green"
                    />
                    <StatCard
                      title="Conversion Rate"
                      value={`${analytics.overview.conversion_rate}%`}
                      subtitle="Inquiries to sales"
                      icon={Target}
                      color="orange"
                    />
                  </div>

                  {/* Marketplace Comparison */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-card border border-border/50 rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-gold" />
                        Performance vs Marketplace
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Avg. Views per Listing</span>
                            <span className={analytics.marketplace_comparison.views_vs_market >= 0 ? 'text-trust-green' : 'text-red-400'}>
                              {analytics.marketplace_comparison.views_vs_market >= 0 ? '+' : ''}{analytics.marketplace_comparison.views_vs_market}%
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-background rounded-full h-2">
                              <div 
                                className="bg-gold h-2 rounded-full" 
                                style={{ width: `${Math.min(100, (analytics.marketplace_comparison.your_avg_views / Math.max(analytics.marketplace_comparison.marketplace_avg_views, 1)) * 50)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-16 text-right">{analytics.marketplace_comparison.your_avg_views}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Marketplace avg: {analytics.marketplace_comparison.marketplace_avg_views}
                          </p>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Avg. Trust Score</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-background rounded-full h-2">
                              <div 
                                className="bg-trust-green h-2 rounded-full" 
                                style={{ width: `${analytics.marketplace_comparison.your_avg_trust_score}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-16 text-right">{analytics.marketplace_comparison.your_avg_trust_score}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Marketplace avg: {analytics.marketplace_comparison.marketplace_avg_trust_score}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-card border border-border/50 rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-trust-green" />
                        Revenue Summary
                      </h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-2 border-b border-border/30">
                          <span className="text-muted-foreground">Total Revenue</span>
                          <span className="font-mono-display font-bold text-xl text-trust-green">
                            GHS {analytics.revenue.total_revenue.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-border/30">
                          <span className="text-muted-foreground">Pending (In Escrow)</span>
                          <span className="font-mono-display font-semibold text-gold">
                            GHS {analytics.revenue.pending_revenue.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-muted-foreground">Completed Sales</span>
                          <span className="font-semibold">{analytics.revenue.completed_transactions}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top Performing Listings */}
                  {analytics.top_listings?.length > 0 && (
                    <div className="bg-card border border-border/50 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-gold" />
                          Top Performing Listings
                        </h3>
                        <Link to="/dashboard">
                          <Button variant="ghost" size="sm" className="text-gold text-xs gap-1">
                            View All <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                      <div>
                        {analytics.top_listings.map((listing, i) => (
                          <ListingPerformanceRow key={listing.listing_id} listing={listing} index={i} />
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Listings Tab */}
                <TabsContent value="listings">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <StatCard
                      title="Active Listings"
                      value={analytics.overview.active_listings}
                      icon={Car}
                      color="gold"
                    />
                    <StatCard
                      title="Sold Listings"
                      value={analytics.overview.sold_listings}
                      icon={ShoppingCart}
                      color="green"
                    />
                    <StatCard
                      title="Avg. Trust Score"
                      value={analytics.overview.avg_trust_score}
                      icon={Shield}
                      color="blue"
                    />
                    <StatCard
                      title="Total Views"
                      value={analytics.overview.total_views}
                      icon={Eye}
                      color="orange"
                    />
                  </div>

                  <div className="bg-card border border-border/50 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">All Listings Performance</h3>
                      <Link to="/create-listing">
                        <Button size="sm" className="bg-gold text-black gap-1">
                          <Car className="w-4 h-4" /> Add Listing
                        </Button>
                      </Link>
                    </div>
                    {analytics.top_listings?.length > 0 ? (
                      <div>
                        {analytics.top_listings.map((listing, i) => (
                          <ListingPerformanceRow key={listing.listing_id} listing={listing} index={i} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10 text-muted-foreground">
                        <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No listings yet. Create your first listing to start tracking performance.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Revenue Tab */}
                <TabsContent value="revenue">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                    <StatCard
                      title="Total Revenue"
                      value={`GHS ${analytics.revenue.total_revenue.toLocaleString()}`}
                      icon={DollarSign}
                      color="green"
                    />
                    <StatCard
                      title="Pending Revenue"
                      value={`GHS ${analytics.revenue.pending_revenue.toLocaleString()}`}
                      subtitle="In escrow"
                      icon={Clock}
                      color="gold"
                    />
                    <StatCard
                      title="Completed Sales"
                      value={analytics.revenue.completed_transactions}
                      icon={ShoppingCart}
                      color="blue"
                    />
                  </div>

                  <div className="bg-card border border-border/50 rounded-xl p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-trust-green" />
                      Revenue Insights
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Your conversion rate of <span className="font-semibold text-foreground">{analytics.overview.conversion_rate}%</span> means 
                          approximately {analytics.overview.conversion_rate} out of every 100 inquiries result in a sale.
                        </p>
                        {analytics.overview.conversion_rate < 10 && (
                          <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 text-sm">
                            <p className="font-medium text-gold mb-1">Tip to improve conversions:</p>
                            <p className="text-muted-foreground">
                              Respond to inquiries quickly, provide detailed vehicle information, and ensure your photos are high quality.
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          You've had <span className="font-semibold text-foreground">{analytics.recent_activity.inquiries_30d}</span> inquiries 
                          in the last 30 days.
                        </p>
                        {analytics.revenue.pending_revenue > 0 && (
                          <div className="bg-trust-green/10 border border-trust-green/30 rounded-lg p-3 text-sm">
                            <p className="font-medium text-trust-green mb-1">Pending funds:</p>
                            <p className="text-muted-foreground">
                              GHS {analytics.revenue.pending_revenue.toLocaleString()} is currently in escrow awaiting buyer confirmation.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </>
            ) : (
              <div className="text-center py-20 text-muted-foreground">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Unable to load analytics. Please try again.</p>
                <Button onClick={fetchAnalytics} variant="outline" size="sm" className="mt-4">
                  Retry
                </Button>
              </div>
            )}
          </Tabs>

          {/* Subscription Management */}
          {isActive && (
            <div className="mt-8 pt-6 border-t border-border/50">
              <button 
                onClick={cancelSubscription}
                className="text-sm text-muted-foreground hover:text-red-400 transition-colors"
              >
                Cancel subscription
              </button>
            </div>
          )}
        </div>
      </main>
    );
  }

  // Show upgrade UI for non-dealers
  return (
    <main data-testid="dealer-dashboard" className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-gold" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Dealer Program</h1>
          <p className="text-muted-foreground">
            Upgrade to dealer status and unlock unlimited listings
          </p>
        </motion.div>

        {/* Expired Warning */}
        {isExpired && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <div>
                <p className="font-medium text-red-400">Trial Expired</p>
                <p className="text-sm text-muted-foreground">
                  Subscribe to continue as a dealer and unlock unlimited listings.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Pricing Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* Free Trial Banner */}
          {neverSubscribed && (
            <div className="bg-trust-green/10 border border-trust-green/30 rounded-xl p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-trust-green/20 flex items-center justify-center">
                    <Gift className="w-6 h-6 text-trust-green" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-trust-green">Try Free for 7 Days</h3>
                    <p className="text-sm text-muted-foreground">
                      Full dealer access. No payment required. Cancel anytime.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={startFreeTrial}
                  disabled={startingTrial}
                  className="bg-trust-green hover:bg-trust-green/90 text-white gap-2 h-11"
                  data-testid="start-trial-btn"
                >
                  {startingTrial ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                  Start Free Trial
                </Button>
              </div>
            </div>
          )}

          {/* Plan Selection */}
          <div className="grid md:grid-cols-3 gap-4">
            {/* Monthly Plan */}
            <div 
              onClick={() => setSelectedPlan('monthly')}
              className={`bg-card border rounded-xl p-5 cursor-pointer transition-all hover:border-gold/50 ${
                selectedPlan === 'monthly' ? 'border-gold ring-2 ring-gold/20' : 'border-border/50'
              }`}
              data-testid="plan-monthly"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-gold" />
                </div>
                {selectedPlan === 'monthly' && (
                  <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                )}
              </div>
              <h3 className="font-semibold mb-1">Monthly</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-2xl font-bold text-gold">GHS 80</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Flexible month-to-month billing
              </p>
            </div>

            {/* Yearly Upfront Plan */}
            <div 
              onClick={() => setSelectedPlan('yearly_upfront')}
              className={`bg-card border rounded-xl p-5 cursor-pointer transition-all hover:border-gold/50 relative ${
                selectedPlan === 'yearly_upfront' ? 'border-gold ring-2 ring-gold/20' : 'border-border/50'
              }`}
              data-testid="plan-yearly-upfront"
            >
              <Badge className="absolute -top-2 right-3 bg-trust-green text-white border-none text-[10px]">
                Save GHS 360!
              </Badge>
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-gold" />
                </div>
                {selectedPlan === 'yearly_upfront' && (
                  <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                )}
              </div>
              <h3 className="font-semibold mb-1">Yearly (Upfront)</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-2xl font-bold text-gold">GHS 600</span>
                <span className="text-sm text-muted-foreground">/year</span>
              </div>
              <p className="text-xs text-muted-foreground">
                GHS 50/mo equivalent. Best value!
              </p>
            </div>

            {/* Yearly Installments Plan */}
            <div 
              onClick={() => setSelectedPlan('yearly_installments')}
              className={`bg-card border rounded-xl p-5 cursor-pointer transition-all hover:border-gold/50 relative ${
                selectedPlan === 'yearly_installments' ? 'border-gold ring-2 ring-gold/20' : 'border-border/50'
              }`}
              data-testid="plan-yearly-installments"
            >
              <Badge className="absolute -top-2 right-3 bg-blue-500 text-white border-none text-[10px]">
                Most Popular
              </Badge>
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                {selectedPlan === 'yearly_installments' && (
                  <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                )}
              </div>
              <h3 className="font-semibold mb-1">Yearly (Installments)</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-2xl font-bold text-gold">GHS 50</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground">
                12 monthly payments of GHS 50
              </p>
            </div>
          </div>

          {/* Subscribe Button & Benefits */}
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="font-medium mb-4">What's included:</h3>
            <ul className="grid md:grid-cols-2 gap-3 mb-6">
              {DEALER_BENEFITS.map((benefit, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    benefit.highlight ? 'bg-gold/20' : 'bg-background'
                  }`}>
                    <benefit.icon className={`w-4 h-4 ${benefit.highlight ? 'text-gold' : 'text-muted-foreground'}`} />
                  </div>
                  <span className={`text-sm ${benefit.highlight ? 'font-medium' : ''}`}>{benefit.text}</span>
                </li>
              ))}
            </ul>

            <div className="pt-4 border-t border-border/50">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Pay with MTN MoMo, Vodafone Cash, or Card</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-yellow-500/10 flex items-center justify-center">
                      <span className="text-yellow-500 font-bold text-[10px]">MTN</span>
                    </div>
                    <div className="w-8 h-8 rounded bg-red-500/10 flex items-center justify-center">
                      <span className="text-red-500 font-bold text-[10px]">Voda</span>
                    </div>
                    <div className="w-8 h-8 rounded bg-blue-500/10 flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-blue-500" />
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Selected plan:</p>
                  <p className="font-semibold text-gold">
                    {selectedPlan === 'monthly' && 'Monthly - GHS 80/mo'}
                    {selectedPlan === 'yearly_upfront' && 'Yearly - GHS 600 (one-time)'}
                    {selectedPlan === 'yearly_installments' && 'Yearly - GHS 50/mo × 12'}
                  </p>
                </div>
              </div>
              
              <Button
                onClick={() => subscribe(selectedPlan)}
                disabled={subscribing}
                className="w-full h-12 bg-gold text-black hover:brightness-110 gap-2"
                data-testid="subscribe-btn"
              >
                {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Subscribe Now
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-3">
                Cancel anytime. Secure payment powered by Paystack.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 grid md:grid-cols-2 gap-4"
        >
          <button
            onClick={() => navigate('/create-listing')}
            className="bg-card border border-border/50 rounded-xl p-4 text-left hover:border-gold/30 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium mb-1">Create Listing</h3>
                <p className="text-sm text-muted-foreground">List a new car for sale</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
            </div>
          </button>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-card border border-border/50 rounded-xl p-4 text-left hover:border-gold/30 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium mb-1">My Listings</h3>
                <p className="text-sm text-muted-foreground">Manage your inventory</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
            </div>
          </button>
        </motion.div>
      </div>
    </main>
  );
}
